import { Logout, PreviewCloseOne } from '@icon-park/react';
import { isBoolean, noop } from 'lodash';
import { memo, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import type {
  EmployeeViewFull,
  StudentViewFull,
  TutorViewFull,
} from '@didaskill/domain/users/types';
import { Avatar, AvatarBlock, Menu, Tag, Typography } from '@didaskill/ui/design-system';

import messages from './ProfilePopoverContent.messages';
import * as styles from './ProfilePopoverContent.styles';

export type ProfilePopoverContentProps = {
  user: StudentViewFull | EmployeeViewFull | TutorViewFull;
  subtitle?: ReactNode;
  onClickLogout?: () => void;
  children?: ReactNode;
};

export const ProfilePopoverContentBase = ({
  user,
  subtitle,
  onClickLogout = noop,
  children,
}: ProfilePopoverContentProps) => {
  const isProfilePrivate =
    'isProfilePublic' in user && isBoolean(user.isProfilePublic) && !user.isProfilePublic;

  const displayName = 'displayName' in user ? user.displayName : null;

  return (
    <div css={styles.popover}>
      {/* Avatar + user name + user status. */}
      <div css={styles.content}>
        <AvatarBlock
          size={Avatar.SIZES.OVERSIZED}
          user={user}
          title={
            <Typography variant={Typography.VARIANTS.HEADING_4}>
              {displayName || user.fullName}
            </Typography>
          }
          subtitle={
            subtitle ||
            (displayName && (
              <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR}>
                ({user.fullName})
              </Typography>
            ))
          }
          status={
            isProfilePrivate && (
              <Tag
                variant={Tag.VARIANTS.NEUTRAL}
                icon={<PreviewCloseOne />}
                label={<FormattedMessage {...messages.PROFILE_PRIVATE} />}
              />
            )
          }
        />
      </div>

      {/* Menu. */}
      {children && <div css={styles.groupedItems}>{children}</div>}

      <Menu>
        <Menu.ItemButton
          icons={<Logout />}
          label={<FormattedMessage {...messages.LOGOUT} />}
          onClick={onClickLogout}
        />
      </Menu>
    </div>
  );
};

ProfilePopoverContentBase.displayName = 'ProfilePopoverContent';

export const ProfilePopoverContent = memo(ProfilePopoverContentBase);
